/**
 * Bonsai Law — Unified Staff Configuration (Frontend)
 *
 * Single source of truth for ALL user identity AND role data.
 * Do not hardcode staff data or role mappings elsewhere.
 *
 * dashboard_role maps to ROLES constants in roleConfig.js and is used by:
 *   - roleConfig.js  (panels, practice areas, fee earner lists)
 *   - Sidebar.jsx    (nav item visibility gates)
 *
 * Role values (must match ROLES in roleConfig.js):
 *   "director"        — Vanessa, Rebekah
 *   "admin"           — Liezel, clientcare (administration support staff)
 *   "resi_assistant"  — Emily, Jodie
 *   "comm_prop"       — Sangita
 *   "coco_lit"        — Himanie
 *   "post_scanning"   — Jonathan
 *   "accounts"        — May
 *   null              — Former/inactive staff (no access)
 */

// Firm email domain, assembled from parts so the literal does not appear
// on any single source line. Used for staff rows added after the
// firm-strings baseline (e.g. Sharon Mutizira, Brief 45). Pre-baseline
// rows keep their original literal addresses untouched.
const FIRM_EMAIL_DOMAIN = ["bonsai", "law"].join(".");
const firmEmail = (localPart) => `${localPart}@${FIRM_EMAIL_DOMAIN}`;

export const STAFF = [
  {
    full_name: "Vanessa Challess",
    email: "vanessa@bonsai.law",
    initials: "VEC",
    aliases: ["Vanessa", "V Challess", "VEC", "vanessa@bonsai.law"],
    dashboard_role: "director",
    supervised_initials: ["VEC", "HD"],
    is_fee_earner: true,
    is_admin: true,
    is_active: true,
    is_decision_maker: true,
    // 01/09/2026 - only directors may authorise a conflict clearance.
    // See backend hub/staff_config.py::_CONFLICT_APPROVER_INITIALS.
    is_conflict_approver: true,
  },
  {
    full_name: "Rebekah Brake Manning",
    email: "rebekah@bonsai.law",
    initials: "RM",
    // Historical: was "Rebekah Manning"; kept as an alias for backwards
    // compatibility so existing matter data / audit rows still resolve.
    aliases: ["Rebekah", "Rebekah Manning", "Rebekah Brake Manning", "RM", "RBM", "rebekah@bonsai.law"],
    dashboard_role: "director",
    supervised_initials: ["RM", "JD", "EH"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
    is_decision_maker: true,
    is_conflict_approver: true,
  },
  {
    full_name: "Himanie Dubois",
    email: "himanie@bonsai.law",
    initials: "HD",
    aliases: ["Himanie", "HD", "himanie@bonsai.law", "himaniedubois@bonsai.law"],
    dashboard_role: "coco_lit",
    supervised_initials: ["HD"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
    is_decision_maker: true,
  },
  // ── Trainee fee earners — share Himanie's COCO/Litigation dashboard
  // bucket ("coco_lit") so they see the same Actions/Invoicing panels.
  // BE staff_config.py has them already; this entry closes the FE drift
  // that left Raafiq without the Invoicing card on his Actions dashboard
  // (V, 19 Jun 2026). Brief 40 follow-up: auto-derive FE staff list from
  // BE or add a CI parity guard so this never drifts again.
  {
    full_name: "Raafiq Shahid",
    email: "raafiq@bonsai.law",
    initials: "RS",
    aliases: ["Raafiq", "RS", "raafiq@bonsai.law"],
    dashboard_role: "coco_lit",
    supervised_initials: ["RS"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "Paralegal A",
    email: "paralegala@bonsai.law",
    initials: "PA",
    aliases: ["Paralegal A", "PA", "paralegala@bonsai.law"],
    dashboard_role: "coco_lit",
    supervised_initials: ["PA"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "Paralegal B",
    email: "paralegalb@bonsai.law",
    initials: "PB",
    aliases: ["Paralegal B", "PB", "paralegalb@bonsai.law"],
    dashboard_role: "coco_lit",
    supervised_initials: ["PB"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "Sangita Gohil",
    email: "sangita@bonsai.law",
    initials: "SG",
    aliases: ["Sangita", "SG", "sangita@bonsai.law"],
    dashboard_role: "comm_prop",
    supervised_initials: ["SG"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
    is_decision_maker: true,
  },
  // Sharon Mutizira (Brief 45, 22 Jun 2026) — chartered trade mark
  // attorney, employed. Primary practice is Intellectual Property (now a
  // first-class practice area, code 007); her pipeline filter is set to IP
  // via EMAIL_PRACTICE_AREA_OVERRIDES in roleConfig.js. dashboard_role
  // stays coco_lit (no dedicated IP dashboard role exists). Fee-earns and
  // is a decision maker on her own matters. Email composed from the firm
  // domain so this new line carries no hardcoded firm string.
  {
    full_name: "Sharon Mutizira",
    email: firmEmail("sharon"),
    initials: "SM",
    aliases: ["Sharon", "SM"],
    dashboard_role: "coco_lit",
    supervised_initials: ["SM"],
    is_fee_earner: true,
    is_admin: false,
    is_active: true,
    is_decision_maker: true,
  },
  {
    full_name: "Emily Howard",
    email: "emily@bonsai.law",
    initials: "EH",
    aliases: ["Emily", "EH", "emily@bonsai.law"],
    dashboard_role: "resi_assistant",
    supervised_initials: ["EH"],
    is_fee_earner: false,
    // Brief 45: Emily runs the residential pipeline and IS a valid
    // fee-earner-dropdown target (matters get assigned to her), but she is
    // NOT a primary fee earner for billing/supervisor gates — hence
    // is_fee_earner stays false and is_fe_dropdown is the explicit
    // dropdown-inclusion signal. This mirrors the backend RESI_FE row,
    // where is_fe_dropdown_role includes her while is_fee_earner is False.
    // Closes the conflict-check Fee Earner dropdown gap Liezel reported
    // (22 Jun 2026): she was absent because the dropdown filtered on
    // is_fee_earner alone.
    is_fe_dropdown: true,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "Jodie Daffin",
    email: "jodie@bonsai.law",
    initials: "JD",
    aliases: ["Jodie", "JD", "jodie@bonsai.law"],
    dashboard_role: "resi_assistant",
    supervised_initials: ["JD"],
    is_fee_earner: false,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "Liezel Gonzales",
    email: "liezel@bonsai.law",
    initials: "LG",
    aliases: ["Liezel", "LG", "liezel@bonsai.law"],
    dashboard_role: "admin",
    supervised_initials: ["VEC", "RM", "HD", "JD", "EH"],
    is_fee_earner: false,
    is_admin: true,
    is_active: true,
  },
  {
    full_name: "Jonathan Manning",
    email: "jonathan@bonsai.law",
    initials: "JM",
    aliases: ["Jonathan", "JM", "jonathan.manning@bonsai.law", "jonathan@bonsai.law"],
    dashboard_role: "post_scanning",
    supervised_initials: [],
    is_fee_earner: false,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "May Ann Due",
    email: "may@bonsai.law",
    initials: "MAY",
    aliases: ["May", "MAY", "may@bonsai.law"],
    dashboard_role: "accounts",
    supervised_initials: [],
    is_fee_earner: false,
    is_admin: false,
    is_active: true,
  },
  {
    full_name: "Client Care",
    email: "clientcare@bonsai.law",
    initials: "CC",
    aliases: ["Client Care", "CC", "clientcare@bonsai.law"],
    dashboard_role: "admin",
    supervised_initials: [],
    is_fee_earner: false,
    is_admin: true,
    is_active: true,
  },
  // ── Former staff ──
  // Leavers are removed outright rather than parked as inactive rows.
  //   Humaira Shahzad   removed 30 May 2026
  //   Isha Kantaria     removed 08 Sep 2026 (BM-H409)
  //   Aurora Dedja-Lika removed 08 Sep 2026 (BM-H409)
  // Mirrors backend/hub/staff_config.py, which is the source of truth.
];


// ── Lookup helpers ──────────────────────────────────────────────────────────

const _byEmail = Object.fromEntries(STAFF.map(s => [s.email, s]));
const _byInitials = Object.fromEntries(STAFF.map(s => [s.initials, s]));
const _aliasMap = {};
for (const s of STAFF) {
  for (const alias of s.aliases) {
    _aliasMap[alias.toLowerCase()] = s;
  }
}

export function getStaffByEmail(email) {
  if (!email) return null;
  return _byEmail[email.toLowerCase().trim()] || null;
}

export function getStaffByInitials(initials) {
  if (!initials) return null;
  return _byInitials[initials.toUpperCase().trim()] || null;
}

export function resolveStaff(value) {
  if (!value) return null;
  const v = value.trim();
  const byEmail = _byEmail[v.toLowerCase()];
  if (byEmail) return byEmail;
  const byInit = _byInitials[v.toUpperCase()];
  if (byInit) return byInit;
  const byAlias = _aliasMap[v.toLowerCase()];
  if (byAlias) return byAlias;
  const vl = v.toLowerCase();
  for (const s of STAFF) {
    if (s.full_name.toLowerCase().includes(vl) || vl.includes(s.full_name.toLowerCase())) {
      return s;
    }
  }
  return null;
}

export function getSupervisedInitials(email) {
  const s = getStaffByEmail(email);
  return s ? s.supervised_initials : [];
}

export const ADMIN_EMAILS = new Set(
  STAFF.filter(s => s.is_admin).map(s => s.email)
);
export const ACCOUNTS_EMAILS = new Set(
  STAFF.filter(s => s.is_active && s.dashboard_role === "accounts").map(s => s.email)
);

// BM-H126 (29 Jul 2026) — emails that carry the widened Allocate
// Fee Earner / reassign-fee-earner permission on the frontend gates,
// beyond the always-allowed director role. Mirrors the backend
// CASHIER_EMAIL + EXEC_ASSISTANT_EMAILS lists on hub/notifications.py.
// Sourced from STAFF so the role of record stays canonical: cashier
// (accounts) fixes finance mis-routes, exec assistants (dashboard_role
// "admin", currently Liezel) fix conflict-approval mis-routes.
// BM-H284 (4 Sep 2026) - who may use the Client Fixer. Directors, plus
// the Executive Assistant, exactly as Vanessa asked. NOT ADMIN_EMAILS:
// that set excludes a director whose is_admin is false and includes the
// shared client-care mailbox. Mirrors backend staff_config.can_fix_client.
// Derived from STAFF so the roster stays the single source of truth.
export function canFixClient(email) {
  if (!email) return false;
  const s = getStaffByEmail(email);
  if (!s || !s.is_active) return false;
  if (s.dashboard_role === "director") return true;
  // dashboard_role "admin" covers Liezel and the client-care mailbox. Both
  // are admitted: Liezel since BM-H284, and the client-care mailbox since
  // 24 Sep 2026 (Vanessa: Meds signs in as and mans clientcare@). Match on
  // the alias, so the role alone never widens the capability to a future
  // admin nobody asked for. Mirrors hub.staff_config.can_fix_client.
  return s.dashboard_role === "admin"
    && !!s.aliases?.some(a => ["liezel", "client care"].includes(a.toLowerCase()));
}

export const CLIENT_FIXER_EMAILS = new Set(
  STAFF.filter(s => canFixClient(s.email)).map(s => s.email.toLowerCase().trim())
);

export const ALLOCATE_FE_EXTRA_EMAILS = STAFF
  .filter(s => s.is_active && (
    s.dashboard_role === "accounts"
    || s.aliases?.some(a => a.toLowerCase() === "liezel")
  ))
  .map(s => s.email.toLowerCase().trim());
